/**
 * Capacitor Native App Initializer
 * Handles native app setup when running on iOS/Android
 * 
 * Security features:
 * - Secure storage for VoIP tokens
 * - Authenticated token registration
 * - Sanitized logging
 */

import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PUSH_ENABLED } from '@/lib/feature-flags';

const log = {
  debug: (...args: unknown[]) => console.debug('[NativeApp]', ...args),
  info: (...args: unknown[]) => console.info('[NativeApp]', ...args),
  warn: (...args: unknown[]) => console.warn('[NativeApp]', ...args),
};
const NATIVE_STARTUP_VERSION = 'native-startup-v2026-05-16-safe-bootstrap';

interface NativeAppProviderProps {
  children: React.ReactNode;
  onPause?: () => void;
  onResume?: () => void;
}

/**
 * Component that initializes native app features
 * Wrap your app with this at the root level
 */
export const NativeAppProvider = ({ 
  children, 
  onPause, 
  onResume 
}: NativeAppProviderProps) => {
  const initialized = useRef(false);
  const pushRegistered = useRef(false);
  const activeRef = useRef(true);
  const isNative = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === 'ios';

  useEffect(() => {
    if (!isNative) return;

    const platform = Capacitor.getPlatform();
    const body = document.body;
    body.classList.add('is-native', `platform-${platform}`);

    let lastHaptic = 0;
    const preventGestureZoom = (event: Event) => event.preventDefault();
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') return;
      const target = event.target as Element | null;
      const interactive = target?.closest?.('button,a,[role="button"],[role="tab"],[role="menuitem"],[role="switch"],[data-haptic]');
      if (!interactive || (interactive as HTMLElement).dataset?.noHaptic !== undefined) return;
      const now = performance.now();
      if (now - lastHaptic < 60) return;
      lastHaptic = now;
      void import('@/lib/native-haptics')
        .then(({ hapticTap }) => hapticTap())
        .catch((error) => log.warn('Haptic startup skipped', error));
    };

    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('pointerdown', onPointerDown, { passive: true });

    return () => {
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isNative]);

  useEffect(() => {
    if (!isNative) return;

    let disposed = false;
    let stateHandle: { remove: () => void } | null = null;
    let backHandle: { remove: () => void } | null = null;
    // Tracks when the app last became inactive. If the WebView was suspended
    // for a long stretch (Android low-memory, iOS aggressive background),
    // resuming sometimes leaves a blank white screen because the React tree
    // is still mounted but the JS thread / rendering pipeline stalled.
    // We detect that with a rAF tick: if rAF does not fire within 1.5s of
    // resume, force a soft reload. (Plan A1, 2026-06-07.)
    let pausedAt: number | null = null;
    const STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes hidden
    const RAF_BUDGET_MS = 1500;

    const probeWebViewAlive = () => {
      let ticked = false;
      const start = performance.now();
      requestAnimationFrame(() => { ticked = true; });
      window.setTimeout(() => {
        if (!ticked) {
          log.warn(`Resume rAF stalled >${RAF_BUDGET_MS}ms - soft reloading to recover from suspended WebView`);
          try { window.location.reload(); } catch { /* noop */ }
        } else {
          log.debug(`Resume rAF healthy in ${Math.round(performance.now() - start)}ms`);
        }
      }, RAF_BUDGET_MS);
    };

    void import('@capacitor/app')
      .then(async ({ App }) => {
        stateHandle = await App.addListener('appStateChange', (state: { isActive: boolean }) => {
          const wasActive = activeRef.current;
          activeRef.current = state.isActive;
          if (state.isActive && !wasActive) {
            log.debug('App resumed');
            // Defensively hide the splash on every resume. iOS shows the
            // LaunchScreen storyboard whenever it re-creates the WebView
            // after suspending the app (overnight, low-memory). Without
            // this call the rounded-icon splash stays on screen because
            // the cold-init hide path (initialized.current guard) does
            // not run again. (Plan A2, 2026-06-10.)
            void import('@/lib/splash-screen')
              .then(({ hideSplashScreen }) => hideSplashScreen(120))
              .catch(() => { /* noop */ });
            const hiddenFor = pausedAt != null ? Date.now() - pausedAt : 0;
            pausedAt = null;
            if (hiddenFor >= STALE_AFTER_MS) {
              log.info(`App hidden for ${Math.round(hiddenFor / 1000)}s - probing WebView health`);
              probeWebViewAlive();
            }
            onResume?.();
          } else if (!state.isActive && wasActive) {
            log.debug('App paused');
            pausedAt = Date.now();
            onPause?.();
          }
        });
        if (disposed) stateHandle.remove();

        if (Capacitor.getPlatform() === 'android') {
          backHandle = await App.addListener('backButton', () => undefined);
          if (disposed) backHandle.remove();
        }
      })
      .catch((error) => log.warn('Lifecycle bridge skipped', error));

    return () => {
      disposed = true;
      stateHandle?.remove();
      backHandle?.remove();
    };
  }, [isNative, onPause, onResume]);


  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initNativeApp = async () => {
      if (!isNative) {
        log.debug('Running in browser mode');
        return;
      }

      log.info(`Initializing on ${Capacitor.getPlatform()}`);

      // Hide the native splash as soon as React has mounted. Permission checks
      // and push setup must never hold the app on a loading screen.
      requestAnimationFrame(() => {
        void import('@/lib/splash-screen')
          .then(({ hideSplashScreen }) => hideSplashScreen(120))
          .catch((error) => log.warn('Splash hide skipped', error));
      });

      // Register for VoIP push on iOS
      if (isIOS) {
        await registerVoIPToken();
      }
    };

    initNativeApp();
  }, [isNative, isIOS]);

  useEffect(() => {
    const onPermissionChange = (event: Event) => {
      const permission = (event as CustomEvent<{ permission?: string }>).detail?.permission;
      if (!permission) return;
      console.info('[NativeContacts] permission changed', { permission });
    };
    window.addEventListener('elefa:native-contacts-permission', onPermissionChange);
    return () => window.removeEventListener('elefa:native-contacts-permission', onPermissionChange);
  }, []);

  /**
   * Register VoIP push token securely
   * - Stores token in secure storage (Keychain)
   * - Sends to backend only when authenticated
   */
  const registerVoIPToken = async () => {
    try {
      const { registerForVoIPPush } = await import('@/hooks/useCallKit');
      const voipToken = await registerForVoIPPush();
      
      if (!voipToken) {
        log.debug('No VoIP token received');
        return;
      }

      // Store in secure storage first (Keychain on iOS)
      const { secureStorage } = await import('@/lib/secure-storage');
      await secureStorage.setVoIPToken(voipToken);
      log.info('VoIP token stored securely');

      // Only send to backend if user is authenticated
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Update user profile with VoIP token
        const { error } = await supabase
          .from('user_profiles_extended')
          .update({
            voip_push_token: voipToken,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);

        if (error) {
          log.warn('Failed to save VoIP token to backend:', { error: error.message });
        } else {
          log.info('VoIP token registered with backend');
        }
      } else {
        log.debug('User not authenticated, token stored locally only');
      }
    } catch (error) {
      log.warn('VoIP registration error:', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  };

  useEffect(() => {
    if (!isNative || pushRegistered.current) return;
    // Push is a placeholder feature in v1.0.1 — do NOT trigger the OS
    // permission dialog until the backing push payload pipeline ships.
    // Flip VITE_PUSH_ENABLED=true to re-enable. See src/lib/feature-flags.ts.
    if (!PUSH_ENABLED) return;

    const timer = window.setTimeout(() => {
      const registerNativePush = async () => {
        if (pushRegistered.current) return;
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { PushNotifications } = await import('@capacitor/push-notifications');
          const perm = await PushNotifications.checkPermissions();
          let receive = perm.receive;
          if (receive === 'prompt' || receive === 'prompt-with-rationale') {
            receive = (await PushNotifications.requestPermissions()).receive;
          }
          if (receive !== 'granted') {
            log.info('Native notification permission denied');
            return;
          }

          await PushNotifications.removeAllListeners();
          await PushNotifications.addListener('registration', async (token) => {
            try {
              const { error } = await supabase.functions.invoke('push-subscribe', {
                body: { native: true, platform: Capacitor.getPlatform(), token: token.value },
              });
              if (error) log.warn('Native token register failed', error.message);
              else log.info('Native push token registered');
            } catch (error) {
              log.warn('Native token POST error', error);
            }
          });
          await PushNotifications.addListener('registrationError', (error) => log.warn('Native registration error', error));
          await PushNotifications.register();
          pushRegistered.current = true;
        } catch (error) {
          log.warn('Native push registration skipped', error);
        }
      };
      void registerNativePush();
    }, 15_000);

    return () => window.clearTimeout(timer);
  }, [isNative]);

  return (
    <>
      <span data-native-startup-version={NATIVE_STARTUP_VERSION} hidden />
      {children}
    </>
  );
};

export default NativeAppProvider;

